<template>
  <div>
    <section-page
      title="Leads"
      full-height
    >
      <template v-slot:right>
        <table-buttons
          show-refresh
          show-presets
          show-download-button
          show-columns-button
          show-filters-button
          :show-actions="showAction"
          :filter-count="filterCount"
          :presets="presets"
          :default-preset="defaultPreset"
          :actions="actions"
          :do-refresh="refreshTable"
          download-label="leads"
          :do-download="downloadCSV"
          :is-downloading="downloading"
          :do-columns-apply="applyColumnsChange"
          :do-columns-reset="resetColumns"
          :do-filters-apply="refreshTable"
          :do-filters-reset="resetFilters"
          :do-manage-presets="managePreset"
          @presetChanged="changePreset"
          @runAction="runAction"
        >
          <template v-slot:columns>
            <div class="mb-50" v-for="(column, index) in filterColumns">
              <b-switch :value="true" v-model="column.visible" size="is-small" :key="index" expanded>{{ column.label }}</b-switch>
            </div>
          </template>

          <template v-slot:filters>
            <leads-filters filter-name="leads"/>
          </template>
        </table-buttons>
      </template>

      <table-paginated
        ref="leadsTable"
        is-checkable
        sticky-checkbox
        show-per-page-option
        page-input
        :load-data="loadLeads"
        :checked-rows-data="checkedActionRowsData"
        :columns="leadsTableColumn"
        :per-page="25"
        :actions-width="100"
        :default-sort="['property_street_address', 'asc']"
        :has-mobile-cards="false"
        empty-heading="You have no leads."
        entries-label="leads"
        @rowsChecked="manageRowsChecked"
      />
    </section-page>

    <section-page
      title="Mass Action Logs"
    >
      <template v-slot:right>
        <table-buttons
          show-refresh
          :do-refresh="refreshActionTable"
        />
      </template>

      <table-paginated
        ref="actionsTable"
        show-per-page-option
        :load-data="loadProcessedMassActions"
        :columns="actionTableColumns"
        :per-page="10"
        empty-heading="You have no action history."
        entries-label="items"
      />
    </section-page>

    <modal-container>
      <modal-default
        ref="managePresetsModal"
        title="Manage Presets"
        is-wide
      >
        <user-default-preset-form/>

        <b-tabs
          v-model="currentTab"
          class="tabs_vertical_modal"
          vertical
          expanded
          :animated="false"
          @input="handleTabChange"
        >
          <b-tab-item
            value="0"
            label="New Preset"
          >
            <preset-form
              v-if="currentTab === 0"
              store-module="leads"
              :preset-id="activePresetId"
            />
          </b-tab-item>

          <b-tab-item
            v-for="(preset, index) in presets"
            :key="preset.id"
            :value="String(index) + 1"
            :label="preset.label"
          >
            <preset-form
              v-if="currentTab === (index + 1)"
              store-module="leads"
              :preset-id="activePresetId"
              @deleted="handleTabChange(0)"
            />
          </b-tab-item>
        </b-tabs>
      </modal-default>
    </modal-container>

    <modal-container>
      <modal-default
        :ref="actions[0].modal"
        title="Update Status"
      >
        <p class="modal-description">Selected {{ checkedActionRowsData.length }} out of {{ totalLeads }} leads</p>

        <form-field-select-status
          label="Status"
          v-model="actionForm.status.id"
          :validation-errors="validationErrors.status_id"
          include-no-status
        ></form-field-select-status>

        <!-- Required Due At Date -->
        <form-field-datetime-picker
          v-if="statusRequiresDueAt"
          label="Status Change Date"
          v-model="actionForm.status.due_at"
          help-text="This status requires you to set a status change date."
          :validation-errors="validationErrors.due_at"
        ></form-field-datetime-picker>

        <!-- Required Next Contact Date -->
        <form-field-datetime-picker
          v-if="statusRequiresNextContactAt"
          label="Next Contact At"
          v-model="actionForm.status.next_contact_at"
          help-text="This status requires you to set a next contact at date."
          :validation-errors="validationErrors.next_contact_at"
        ></form-field-datetime-picker>

        <template v-slot:footer>
          <button-default @click="saveStatus" :loading="saving" :disabled="saving">
            Save
          </button-default>
        </template>
      </modal-default>
    </modal-container>

    <modal-container>
      <modal-default
        :ref="actions[1].modal"
        title="Update Campaign"
      >
        <p class="modal-description">Selected {{ checkedActionRowsData.length }} out of {{ totalLeads }} leads</p>

        <form-field-select
          label="Campaign"
          v-model="actionForm.campaign_id"
          :validation-errors="validationErrors.campaign_id"
        >
          <option :value="null">No Campaign</option>
          <option v-for="campaign in activeCampaigns" :value="campaign.id">{{ campaign.label }}</option>
        </form-field-select>

        <template v-slot:footer>
          <button-default @click="saveCampaign" :loading="saving" :disabled="saving">
            Save
          </button-default>
        </template>
      </modal-default>
    </modal-container>

    <modal-container>
      <modal-default
        :ref="actions[2].modal"
        title="Update Temperature"
      >
        <p class="modal-description">Selected {{ checkedActionRowsData.length }} out of {{ totalLeads }} leads</p>

        <form-field-select
          label="Temperature"
          v-model="actionForm.temperature"
          :validation-errors="validationErrors.temperature"
        >
          <option v-for="option in ENUMS.TemperatureEnum" :value="option.value">{{ option.label }}</option>
        </form-field-select>

        <template v-slot:footer>
          <button-default @click="saveTemperature" :loading="saving" :disabled="saving">
            Save
          </button-default>
        </template>
      </modal-default>
    </modal-container>

    <modal-container>
      <modal-default
        :ref="actions[3].modal"
        title="Update Exit Strategy"
      >
        <p class="modal-description">Selected {{ checkedActionRowsData.length }} out of {{ totalLeads }} leads</p>

        <form-field-select
          label="Exit Strategy"
          v-model="actionForm.exit_strategy"
          :validation-errors="validationErrors.exit_strategy"
        >
          <option v-for="option in ENUMS.ExitStrategyEnum" :value="option.value">{{ option.label }}</option>
        </form-field-select>

        <template v-slot:footer>
          <button-default @click="saveExitStrategy" :loading="saving" :disabled="saving">
            Save
          </button-default>
        </template>
      </modal-default>
    </modal-container>
  </div>
</template>

<script>
import TablePaginated from "../../../components/table/TablePaginated.vue";
import PresetForm from "../../../forms/PresetForm.vue";
import LeadsFilters from "./LeadsFilters.vue";
import TableButtons from "../../../components/table/TableButtons.vue";
import UserDefaultPresetForm from "../../../forms/UserDefaultPresetForm.vue";
import {ENUMS} from "../../../services/enums";
import dates from "../../../services/dates";
import utils from "../../../services/utils";
import {SnackbarProgrammatic as Snackbar} from 'buefy';
import api from "../../../api";

export default {
  name: "LeadsPageContent",
  components: {
    UserDefaultPresetForm,
    TableButtons,
    LeadsFilters,
    PresetForm,
    TablePaginated
  },
  data() {
    return {
      saving: false,
      downloading: false,
      showAction: false,
      totalLeads: 0,
      currentTab: 0,
      filterCount: 0,
      activePresetId: 0,
      actionTableColumns: [
        {
          label: 'Street Address',
          field: 'property_street_address',
        },
        {
          label: 'Action',
          field: 'type',
          display(row) {
            const statusEnum = Object.values(ENUMS.RecordMassActionTypeEnum).find(type => type.value === row.type)
            return (statusEnum) ? `Change ${statusEnum.label}` : '--'
          }
        },
        {
          label: 'Status',
          field: 'queueable_status',
          display(row) {
            const statusEnum = Object.values(ENUMS.QueueableStatusEnum).find(status => status.value === row.queueable_status)
            return (statusEnum) ? statusEnum.label : '--'
          }
        },
        {
          label: 'Date & Time',
          display(row) {
            let datetime = row.processed_at

            if (row.queueable_status === ENUMS.QueueableStatusEnum.Errored.value) datetime = row.errored_at
            if (row.queueable_status === ENUMS.QueueableStatusEnum.Completed.value) datetime = row.completed_at

            return (datetime) ? dates.formatAsDateTime(datetime) : '--'
          }
        },
      ],
      leadsTableColumn: [],
      filterColumns: {},
      validationErrors: {},
      checkedActionRowsData: [],
      actionForm: {
        status: {
          id: null,
          due_at: null,
          next_contact_at: null
        },
        campaign_id: null,
        temperature: null,
        exit_strategy: null,
      },
      actions: [
        { id: 0, label: 'Change Status', modal: 'statusModal' },
        { id: 1, label: 'Change Campaign', modal: 'campaignModal' },
        { id: 2, label: 'Change Temperature', modal: 'temperatureModal' },
        { id: 3, label: 'Change Exit Strategy', modal: 'exitStrategyModal' },
      ],
    };
  },
  created() {
    this.$store.dispatch("leads/setup", "leads")
      .then(() => {
        this.setupColumns()
      })
  },
  computed: {
    allStatuses() {
      return this.$store.getters['pipeline/statuses']
    },
    statusRequiresDueAt() {
      const status = this.allStatuses.find(s => s.id === this.actionForm.status.id)

      if (! status) {
        this.actionForm.status.due_at = null
        return false
      }

      let currentDate = new Date()
      this.actionForm.status.due_at = currentDate.setHours(currentDate.getHours() + 24)

      return status.state === ENUMS.StatusStateEnum.Active.value && !status.acceptable_time
    },
    statusRequiresNextContactAt() {
      const status = this.allStatuses.find(s => s.id === this.actionForm.status.id)
      const contacting = ENUMS.StatusStateEnum.Contacting.value
      const followUp = ENUMS.StatusStateEnum.FollowUp.value

      if(status && (status.state === contacting || status.state === followUp)) {
        let currentDate = new Date()
        this.actionForm.status.next_contact_at = currentDate.setHours(currentDate.getHours() + 24)

        return true
      }

      this.actionForm.status.next_contact_at = null
      return false
    },
    activeCampaigns() {
      return this.$store.getters.activeCampaigns
    },
    presets() {
      return this.$store.getters.leadsTableConfigurations
    },
    defaultPreset() {
      return this.presets.find(preset => preset.id === this.$store.getters.authUser.default_leads_table_preset_id)
    },
  },
  methods: {
    refreshTable() {
      return this.$refs.leadsTable.queryData();
    },
    refreshActionTable() {
      return this.$refs.actionsTable.queryData();
    },
    setupColumns() {
      // shallow copy columns state from leads store
      this.leadsTableColumn = Object.values(this.$store.getters["leads/columns"]("leads"))
      // deep copy columns state from leads store. copying object without affecting original object
      this.filterColumns = utils.clone(this.$store.getters["leads/columns"]("leads"))
    },
    resetColumns() {
      for (let columnsKey in this.filterColumns) {
        this.filterColumns[columnsKey].visible = true
      }
    },
    applyColumnsChange() {
      for (let columnsKey in this.filterColumns) {
        const filterColumn = this.filterColumns[columnsKey]
        const tableColumn = this.leadsTableColumn.find(tableColumn => tableColumn.field === filterColumn.field)

        this.$store.commit("leads/setOneColumn", ["leads", columnsKey, filterColumn.visible])

        if (tableColumn) tableColumn.visible = filterColumn.visible
      }
    },
    resetFilters() {
      this.$store.commit("leads/resetFilters", "leads")
    },
    downloadCSV(isSelected = false) {
      const filters = utils.clone(this.$store.getters["leads/filters"]("leads"))
      filters.download = true
      filters.sort_field = this.$refs.leadsTable.sortInfo.field
      filters.sort_order = this.$refs.leadsTable.sortInfo.order
      filters.columns = this.leadsTableColumn.filter(column => !!column.visible)
        .map(column => { return {class: column.class, label: column.label, field: column.field } })

      if (isSelected) {
        const recordIds = this.getRecordIds()
        if (recordIds.length === 0) {
          Snackbar.open({
            message: "No leads were selected",
            type: 'is-danger',
            position: 'is-bottom',
          })
          return
        }

        filters.record_ids = recordIds
      }

      Object.keys(filters).forEach((key) => {
        if (filters[key] === null) delete filters[key]

        else if (filters[key] === '') delete filters[key]

        else if (filters[key].length === 0) delete filters[key]
      })

      this.downloading = true
      api.leadsTable.get(filters)
        .then(res => {
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(new Blob([res.data]))
          link.setAttribute('download', 'leads.csv')
          document.body.appendChild(link)
          link.click()
        })
        .finally(() => this.downloading = false)
    },
    changePreset(preset) {
      if (! preset) {
        this.resetColumns()
        this.resetFilters()
      } else {
        for (let columnKey in this.filterColumns) {
          const filterColumn = this.filterColumns[columnKey]
          const presetColumn = preset.columns.find(column => column.field === filterColumn.field)

          if (presetColumn) filterColumn.visible = presetColumn.visible
        }

        for (const [filterKey, filterValue] of Object.entries(preset.filters)) {
          this.$store.commit("leads/setOneFilter", ["leads", filterKey, filterValue])
        }
      }

      this.refreshTable()
    },
    managePreset() {
      this.resetActiveTabPreset()
      this.$refs.managePresetsModal.open()
    },
    handleTabChange(tabIndex) {
      if (tabIndex === 0) {
        this.resetActiveTabPreset()
        return
      }

      this.activePresetId = this.presets[tabIndex - 1].id
    },
    resetActiveTabPreset() {
      this.currentTab = 0
      this.activePresetId = 0
    },
    manageRowsChecked(rows) {
      if (rows.length === 0) {
        this.resetActionForm()
        return
      }

      this.showAction = true
      this.checkedActionRowsData = rows
    },
    runAction(action) {
      if (!action) return

      this.$refs[action.modal].open()
    },
    getRecordIds() {
      return this.checkedActionRowsData.map(row => row.id)
    },
    resetActionForm() {
      this.showAction = false
      this.checkedActionRowsData = []
      this.actionForm = {
        status: {
          id: null,
          due_at: null,
          next_contact_at: null
        },
        campaign_id: null,
        temperature: null,
        exit_strategy: null,
      }
    },
    saveStatus() {
      if (this.statusRequiresDueAt) {
        this.actionForm.status.due_at = new Date(this.actionForm.status.due_at);
        this.actionForm.status.due_at.toISOString();
      } else {
        this.actionForm.status.due_at = null;
      }

      if (this.statusRequiresNextContactAt) {
        this.actionForm.status.next_contact_at = new Date(this.actionForm.status.next_contact_at);
        this.actionForm.status.next_contact_at.toISOString();
      } else {
        this.actionForm.status.next_contact_at = null;
      }

      this.saving = true
      api.records.bulkUpdateStatus({
        record_ids: this.getRecordIds(),
        status_id: this.actionForm.status.id,
        due_at: this.actionForm.status.due_at,
        next_contact_at: this.actionForm.status.next_contact_at
      })
        .then(res => {
          this.$buefy.toast.open({
            message: 'Status change action successfully ran. This might take time due to massive data being processed',
            type: 'is-success',
            duration: 2500,
          })

          this.resetActionForm()
          this.$refs[this.actions[0].modal].close()
        })
        .catch((error) => {
          if (error.response.status === 422) this.validationErrors = error.response.data.errors
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveCampaign() {
      this.saving = true
      api.records.bulkUpdateCampaign({
        record_ids: this.getRecordIds(),
        campaign_id: this.actionForm.campaign_id
      })
        .then(res => {
          this.$buefy.toast.open({
            message: 'Campaign action successfully ran. This might take time due to massive data being processed',
            type: 'is-success',
            duration: 2500,
          })

          this.resetActionForm()
          this.$refs[this.actions[1].modal].close()
        })
        .catch((error) => {
          if (error.response.status === 422) this.validationErrors = error.response.data.errors
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveTemperature() {
      this.saving = true
      api.records.bulkUpdateTemperature({
        record_ids: this.getRecordIds(),
        temperature: this.actionForm.temperature
      })
        .then(res => {
          this.$buefy.toast.open({
            message: 'Temperature action successfully ran. This might take time due to massive data being processed',
            type: 'is-success',
            duration: 2500,
          })

          this.resetActionForm()
          this.$refs[this.actions[2].modal].close()
        })
        .catch((error) => {
          if (error.response.status === 422) this.validationErrors = error.response.data.errors
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveExitStrategy() {
      this.saving = true
      api.records.bulkUpdateExitStrategy({
        record_ids: this.getRecordIds(),
        exit_strategy: this.actionForm.exit_strategy
      })
        .then(res => {
          this.$buefy.toast.open({
            message: 'Exit Strategy action successfully ran. This might take time due to massive data being processed',
            type: 'is-success',
            duration: 2500,
          })

          this.resetActionForm()
          this.$refs[this.actions[3].modal].close()
        })
        .catch((error) => {
          if (error.response.status === 422) this.validationErrors = error.response.data.errors
        })
        .finally(() => {
          this.saving = false
        })
    },
    loadLeads(query = '') {
      const filters = utils.clone(this.$store.getters["leads/filters"]("leads"))
      filters.page = 1

      if (query.length !== 0) {
        const queryArray =  query.split('&');
        filters.page = queryArray.find(str => str.includes('page=')).split('=')[1]
        filters.per_page = queryArray.find(str => str.includes('per_page=')).split('=')[1]
        filters.sort_field = queryArray.find(str => str.includes('sort_field=')).split('=')[1]
        filters.sort_order = queryArray.find(str => str.includes('sort_order=')).split('=')[1]
      }

      Object.keys(filters).forEach((key) => {
        if (filters[key] === null) delete filters[key]
        else if (filters[key] === '') delete filters[key]
        else if (filters[key].length === 0) delete filters[key]
      })

      this.filterCount = this.$store.getters["leads/filterCount"]
      return api.leadsTable.get(filters)
        .then((res) => {
          this.totalLeads = res.data.meta.total
          this.applyColumnsChange()
          return res
        })
    },
    loadProcessedMassActions(query = '') {
      return api.leadsTable.getProcessedMassActions(query)
    }
  },
}
</script>

<style scoped lang="scss">
.modal-description {
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: normal;
}
</style>
